import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import { toast } from 'react-toastify';

import { getInputGroups, initFormValues } from '../../../utils/form/acceptForm';
import {
  acceptAppointment,
  acceptGeneralRequest,
} from '../../../services/appointments';
import {
  Appointment,
  GeneralRequest,
  InputGroupType,
  LoadedUser,
} from '../../../interfaces';
import useConfirm from '../../../hooks/useConfirm';
import useOnschedResource from '../../../hooks/employees/useOnschedResource';
import useEmployeeDurationValidation from '../../../hooks/employees/useEmployeeDurationValidation';
import useUser from '../../../hooks/global/useUser';
import { getArtistOptions } from '../../../services/business';
import { getArtistName } from '../../../utils/global/helper';
import InputGroup from '../InputGroup';
import { EmployeeAvailabilityError } from '../../../types/employees';
import { validatePrice } from '../../../helpers/appointments';
import {
  FIFTEEN_MIN_LENGTH_OPTIONS,
  LENGTH_OPTIONS,
} from '../../../constants/appointments';
import { acceptFormSchema, AcceptFormState } from '../../../types/appointments';

const fifteenMinuteBusinessIds = [
  'cm33kw3wl01gf1k0za6d87mq6',
  'cm51byiny0043ntqghi8j4rwm',
  'cm3xsygqa0076ybmqfv1ozx4b',
  'cm4jjdgzb005yiaanc6quzb32',
  'cm01dmawi009uyhu4jbmdiqo2',
];

interface Props {
  refreshData: (
    appt: Appointment | GeneralRequest,
    action?: 'accept' | 'reject',
  ) => void | Promise<void>;
  closeModals: () => void;
  formError?: string;
  setFormError?: (error: string) => void;
  appointment: Appointment | GeneralRequest | undefined;
}

function FormComponent() {
  const { user: loadedUser } = useUser<LoadedUser>();
  const { values, isSubmitting, isValid, setFieldValue } =
    useFormikContext<AcceptFormState>();
  const [inputGroups, setInputGroups] = React.useState<InputGroupType[]>([]);

  const confirm = useConfirm();
  const { data, isFetching } = useOnschedResource(values.artist);
  const { validate } = useEmployeeDurationValidation({
    availability: data?.availability,
    isLoading: isFetching,
    employeeId: values.artist,
  });

  const useFifteenMinLengthOptions = fifteenMinuteBusinessIds.includes(
    loadedUser.business.id,
  );

  const appointmentLengthOptions = useFifteenMinLengthOptions
    ? FIFTEEN_MIN_LENGTH_OPTIONS
    : LENGTH_OPTIONS.NEW_APPOINTMENT!;

  React.useEffect(() => {
    async function initializeFilters() {
      const tempArtistOptions =
        loadedUser?.role === 'EMPLOYEE'
          ? [
              {
                label: getArtistName(loadedUser!.employee),
                value: loadedUser!.userId,
              },
            ]
          : getArtistOptions(loadedUser!);

      // Create the input groups with the artist and length dropdowns
      const tempInputGroups = await getInputGroups(
        tempArtistOptions,
        appointmentLengthOptions,
        loadedUser!.employee,
        values,
        {
          isLoading: isFetching,
          // return true to save new appointment length, false to cancel
          validateApptLength: async (duration: string) => {
            try {
              const error = validate(Number(duration));
              if (!error) return true;
              const positiveText = error.type === 'warning' ? 'Continue' : 'Ok';
              const negativeText =
                error.type === 'warning' ? 'Cancel' : undefined;
              const confirmed = await confirm({
                dismissible: error.type === 'warning',
                title: error.errorTitle,
                message: error.errorMessage,
                positiveText,
                negativeText,
              });
              if (error.code === EmployeeAvailabilityError.InvalidDuration) {
                const isMaxInList = appointmentLengthOptions.some(
                  (option: any) =>
                    String(option?.value) === String(error.maximumDuration),
                );
                if (isMaxInList) {
                  setFieldValue(
                    'appointmentLengthInMinutes',
                    String(error.maximumDuration || ''),
                  );
                }
              } else if (error.type === 'warning' && confirmed) {
                setFieldValue('appointmentLengthInMinutes', duration);
              }
              return false;
            } catch {
              return false;
            }
          },
        },
      );

      // Set state variables for resource map and input groups
      setInputGroups(tempInputGroups);
    }

    if (loadedUser) {
      initializeFilters();
    }
  }, [
    confirm,
    isFetching,
    loadedUser,
    setFieldValue,
    validate,
    values,
    appointmentLengthOptions,
  ]);

  // reset duration when changing artist
  React.useEffect(() => {
    if (values?.artist) setFieldValue('appointmentLengthInMinutes', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.artist]);

  React.useEffect(() => {
    if (values?.schedulingType === 'FIRST_SESSION') {
      setFieldValue('depositPlacement', 'FIRST');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.schedulingType]);

  return (
    <Form>
      {inputGroups.map((inputGroup: InputGroupType) => (
        <InputGroup
          key={inputGroup.name}
          name={inputGroup.name}
          rows={inputGroup.inputRows}
        />
      ))}
      <Button
        type="submit"
        disabled={isSubmitting || !isValid}
        variant="contained"
        className="primary">
        Submit
      </Button>
    </Form>
  );
}

function AcceptRequestForm({
  refreshData,
  closeModals,
  formError,
  setFormError,
  appointment,
}: Props) {
  const { user: loadedUser } = useUser<LoadedUser>();

  const loadedFormValues = {
    ...initFormValues,
    artist:
      !initFormValues.artist && (appointment as Appointment)?.employeeId
        ? (appointment as Appointment)?.employeeId
        : '',
    customDesignMessage: loadedUser?.employee.standardDesignMessage || '',
  };

  const accept = React.useCallback(
    async (values: AcceptFormState) => {
      try {
        if (!appointment) throw new Error('Appointment not found');

        const appointmentType =
          'appointmentType' in appointment
            ? appointment.appointmentType
            : 'NEW_APPOINTMENT';
        const priceError = validatePrice(values, appointmentType);
        if (priceError) {
          setFormError?.(priceError.error);
          return;
        }

        if ((appointment as Appointment)?.appointmentId) {
          await acceptAppointment(appointment as Appointment, values);
          await refreshData(appointment, 'accept');
          closeModals();
          setFormError?.('');
        } else {
          await acceptGeneralRequest(appointment as GeneralRequest, values);
          await refreshData(appointment, 'accept');
          closeModals();
          setFormError?.('');
          window.scrollTo(0, 0);
        }
        toast.success('Appointment Accepted!');
      } catch {
        setFormError?.(
          'Error updating appointment details. Please refresh the page and try again',
        );
        toast.error('Error accepting appointment');
      }
    },
    [appointment, closeModals, refreshData, setFormError],
  );

  return (
    <Box>
      {formError && (
        <Typography variant="body1" color="primary">
          {formError}
        </Typography>
      )}
      <Formik
        enableReinitialize
        initialValues={loadedFormValues}
        validationSchema={acceptFormSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await accept(values);
          setSubmitting(false);
        }}>
        <FormComponent />
      </Formik>
    </Box>
  );
}

export default AcceptRequestForm;
